<template>
    <v-card variant="flat">
        <v-toolbar flat>
            <v-toolbar-title>Escáner QR (Entrada/Salida automática)</v-toolbar-title>
            <v-spacer />

            <v-chip v-if="running" color="success" class="mr-2" size="small">
                Activo
            </v-chip>
            <v-chip v-else color="grey" class="mr-2" size="small">
                Parado
            </v-chip>

            <v-btn variant="text" @click="startScanner" :disabled="running || starting" :loading="starting">
                Iniciar
            </v-btn>
            <v-btn variant="text" color="red" @click="stopScanner" :disabled="!running">
                Parar
            </v-btn>
        </v-toolbar>

        <v-card-text>
            <v-alert v-if="infoText" :type="infoType" density="compact" class="mb-4">
                {{ infoText }}
            </v-alert>

            <v-row>
                <v-col cols="12" md="5">
                    <v-select v-model="selectedDeviceId" :items="cameraOptions" item-title="label" item-value="deviceId"
                        label="Cámara" density="compact" variant="outlined" :disabled="running" />

                    <v-switch v-model="autoStart" color="primary" label="Auto-iniciar al cargar" class="mt-2" />

                    <v-switch v-model="keepAwake" color="primary" label="Mantener pantalla activa (si es compatible)"
                        class="mt-0" />

                    <v-divider class="my-4" />

                    <div class="text-subtitle-2 mb-2">Último registro</div>
                    <div v-if="lastResult">
                        <v-chip
                            :color="lastResult.status === 'entry' ? 'green' : (lastResult.status === 'exit' ? 'blue' : 'grey')"
                            size="small" class="mb-2">
                            {{ lastResult.status }}
                        </v-chip>

                        <div class="text-body-2">
                            <strong>{{ lastResult.usuario }}</strong>
                        </div>
                        <div class="text-body-2">
                            {{ lastResult.curso }}
                        </div>
                        <div class="text-body-2" v-if="lastResult.entry_at">
                            Entrada: {{ formatDate(lastResult.entry_at) }}
                        </div>
                        <div class="text-body-2" v-if="lastResult.exit_at">
                            Salida: {{ formatDate(lastResult.exit_at) }}
                        </div>
                    </div>
                    <div v-else class="text-body-2 text-medium-emphasis">
                        Aún no hay lecturas
                    </div>

                    <v-divider class="my-4" />

                    <div class="text-subtitle-2 mb-2">Debug (opcional)</div>
                    <v-text-field v-model="manualQr" label="Pegar combined_codiqr manualmente" density="compact"
                        variant="outlined" />
                    <v-btn variant="outlined" @click="sendQr(manualQr)" :disabled="!manualQr || posting"
                        :loading="posting">
                        Enviar manual
                    </v-btn>
                </v-col>

                <v-col cols="12" md="7">
                    <div class="mb-2 text-subtitle-2">Vista cámara</div>

                    <div style="position: relative;">
                        <video ref="videoEl" muted playsinline
                            style="width: 100%; border-radius: 12px; background: #000;"></video>

                        <div v-if="running"
                            style="position:absolute; inset: 0; border-radius: 12px; pointer-events: none;">
                            <!-- marco simple -->
                            <div
                                style="position:absolute; inset: 10%; border: 2px solid rgba(255,255,255,0.6); border-radius: 12px;">
                            </div>
                        </div>
                    </div>

                    <div class="text-caption text-medium-emphasis mt-2">
                        Requiere HTTPS y permiso de cámara. Si el dispositivo es “kiosko”, lo ideal es usar una cuenta
                        de escáner con permisos limitados.
                    </div>
                </v-col>
            </v-row>
        </v-card-text>
    </v-card>

    <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="4000">
        {{ snackbarText }}
    </v-snackbar>
</template>

<script>
import { axios } from '../axios'
import { BrowserQRCodeReader, BrowserCodeReader } from '@zxing/browser'

export default {
    name: 'QrScanner',
    data() {
        return {
            running: false,
            starting: false,
            posting: false,

            codeReader: null,
            controls: null,

            cameraOptions: [],
            selectedDeviceId: null,

            infoText: '',
            infoType: 'info',

            lastResult: null,

            // anti-duplicados
            lastSeenText: null,
            lastSeenAt: 0,

            // opciones
            autoStart: true,
            keepAwake: true,
            wakeLock: null,

            manualQr: '',

            snackbar: false,
            snackbarText: '',
            snackbarColor: 'success',
        }
    },
    methods: {
        setInfo(text, type = 'info') {
            this.infoText = text
            this.infoType = type
        },
        toast(text, color = 'success') {
            this.snackbarText = text
            this.snackbarColor = color
            this.snackbar = true
        },
        formatDate(isoString) {
            try {
                const d = new Date(isoString)
                return d.toLocaleString()
            } catch {
                return isoString
            }
        },
        async loadCameras() {
            // OJO: los labels suelen venir vacíos hasta que el usuario concede permisos
            const devices = await BrowserCodeReader.listVideoInputDevices()
            this.cameraOptions = devices.map(d => ({
                deviceId: d.deviceId,
                label: d.label || 'Cámara',
            }))

            if (!this.selectedDeviceId) {
                // intenta elegir trasera si el label lo permite
                const back = devices.find(d => /back|rear|environment/i.test(d.label || ''))
                this.selectedDeviceId = (back && back.deviceId) || (devices[0] && devices[0].deviceId) || null
            }
        },
        async requestWakeLock() {
            if (!this.keepAwake) return
            try {
                if ('wakeLock' in navigator) {
                    this.wakeLock = await navigator.wakeLock.request('screen')
                }
            } catch {
                // si falla, no pasa nada
            }
        },
        async releaseWakeLock() {
            try {
                if (this.wakeLock) {
                    await this.wakeLock.release()
                    this.wakeLock = null
                }
            } catch {
                // ignore
            }
        },
        async ensureCameraPermission() {
            if (!navigator.mediaDevices?.getUserMedia) {
                throw new Error('Este navegador no soporta getUserMedia (cámara).')
            }

            // Esto fuerza el prompt de permisos y hace que la lista de cámaras aparezca
            const stream = await navigator.mediaDevices.getUserMedia({ video: true })
            stream.getTracks().forEach(t => t.stop())
        },

        async startScanner() {
            if (this.running || this.starting) return
            this.starting = true
            this.setInfo('Solicitando permiso de cámara...', 'info')

            try {
                this.codeReader = new BrowserQRCodeReader()

                // 1) Forzar permiso (si no, a veces devices = [])
                await this.ensureCameraPermission()

                // 2) Ahora sí, listar cámaras
                await this.loadCameras()

                const video = this.$refs.videoEl
                if (!video) throw new Error('Elemento de vídeo no disponible')

                await this.requestWakeLock()

                // 3) Si hay deviceId, úsalo. Si no, fallback a constraints (default camera)
                if (this.selectedDeviceId) {
                    this.controls = await this.codeReader.decodeFromVideoDevice(
                        this.selectedDeviceId,
                        video,
                        async (result) => {
                            if (!result) return
                            await this.handleScan(result.getText())
                        }
                    )
                } else {
                    // Fallback: usa la cámara por defecto (idealmente trasera)
                    this.controls = await this.codeReader.decodeFromConstraints(
                        { video: { facingMode: { ideal: 'environment' } } },
                        video,
                        async (result) => {
                            if (!result) return
                            await this.handleScan(result.getText())
                        }
                    )
                }

                this.running = true
                this.setInfo('Escáner activo. Apunta al QR.', 'success')
            } catch (e) {
                // Mensajes más útiles según el error
                const name = e?.name || ''
                let msg = e?.message || 'No se pudo iniciar el escáner'

                if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
                    msg = 'Permiso de cámara denegado. Revisa permisos del navegador.'
                } else if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
                    msg = 'No se detecta ninguna cámara (o el sistema la está bloqueando).'
                } else if (name === 'NotReadableError' || name === 'TrackStartError') {
                    msg = 'La cámara está siendo usada por otra aplicación (Zoom/Teams/etc.).'
                } else if (!window.isSecureContext) {
                    msg = 'La cámara requiere HTTPS o localhost. Estás en un contexto no seguro.'
                }

                this.setInfo(msg, 'error')
                this.toast(msg, 'error')
                await this.releaseWakeLock()
            } finally {
                this.starting = false
            }
        },

        async stopScanner() {
            try {
                this.controls?.stop()
            } catch {
                // ignore
            }
            this.controls = null
            this.running = false
            this.setInfo('Escáner parado.', 'info')
            await this.releaseWakeLock()
        },

        async handleScan(combined_codiqr) {
            // Anti-duplicados (cámara leyendo el mismo QR muchas veces)
            const now = Date.now()
            if (this.lastSeenText === combined_codiqr && now - this.lastSeenAt < 1200) {
                return
            }
            this.lastSeenText = combined_codiqr
            this.lastSeenAt = now

            await this.sendQr(combined_codiqr)
        },

        async sendQr(combined_codiqr) {
            if (!combined_codiqr) return
            if (this.posting) return

            this.posting = true
            try {
                // Ajusta la ruta si hace falta (p.ej. 'qr/scan-auto/' o 'api/qr/scan-auto/')
                const res = await axios.post('scan_qr_auto/', { combined_codiqr })

                // Esperado: {status: entry/exit/duplicate, ...}
                this.lastResult = res.data

                if (res.data.status === 'entry') {
                    this.toast(`Entrada registrada: ${res.data.usuario}`, 'success')
                } else if (res.data.status === 'exit') {
                    this.toast(`Salida registrada: ${res.data.usuario}`, 'info')
                } else if (res.data.status === 'duplicate') {
                    // silencioso o aviso suave
                    // this.toast('Duplicado ignorado', 'warning')
                } else {
                    this.toast('Respuesta recibida', 'success')
                }
            } catch (err) {
                const detail = err?.response?.data?.detail || 'Error al registrar el acceso'
                this.toast(detail, 'error')
            } finally {
                this.posting = false
            }
        },
    },

    async mounted() {
        // Carga lista de cámaras (puede venir sin labels hasta que des permiso)
        try {
            await this.loadCameras()
        } catch {
            // ignore
        }

        if (this.autoStart) {
            await this.startScanner()
        }
    },

    async beforeUnmount() {
        await this.stopScanner()
    },
}
</script>